import { UUID } from "crypto";

import { useMemo } from "react";

import { create } from "zustand";

import { Role } from "@demo/types/base";
import type { DocumentInputContentItem } from "@demo/types/documentContent";

interface SavedDocumentRoleContent {
  [Role.SYSTEM]: DocumentInputContentItem[];
  [Role.USER]: DocumentInputContentItem[];
}

/**
 * Simple store for original DocumentContent from API responses.
 * Used for accurate dirty state comparison.
 */
interface DocumentContentStore {
  // Store original DocumentContent by document UUID and role
  savedDocumentContent: Record<UUID, SavedDocumentRoleContent>;
  setSavedDocumentContent: (documentUuid: UUID, role: Role, content: DocumentInputContentItem[]) => void;
  getSavedDocumentContent: (documentUuid: UUID, role: Role) => DocumentInputContentItem[];
  clearDocumentContent: (documentUuid: UUID) => void;
}

export const useDocumentContentStore = create<DocumentContentStore>((set, get) => ({
  savedDocumentContent: {},

  setSavedDocumentContent: (documentUuid: UUID, role: Role, content: DocumentInputContentItem[]) => {
    set((state) => ({
      savedDocumentContent: {
        ...state.savedDocumentContent,
        [documentUuid]: {
          ...state.savedDocumentContent[documentUuid],
          [role]: content,
        },
      },
    }));
  },

  getSavedDocumentContent: (documentUuid: UUID, role: Role): DocumentInputContentItem[] => {
    const content = get().savedDocumentContent[documentUuid]?.[role];

    if (!content) {
      console.warn(`No saved document content found for documentUuid: ${documentUuid}, role: ${role}`);
      return [];
    }

    return content;
  },

  clearDocumentContent: (documentUuid: UUID) => {
    set((state) => {
      const { [documentUuid]: removed, ...rest } = state.savedDocumentContent;
      return { savedDocumentContent: rest };
    });
  },
}));

/**
 * Reactive hook to get saved document content that automatically re-renders on changes
 */
export const useSavedDocumentContent = (documentUuid: UUID | null, role: Role): DocumentInputContentItem[] => {
  const savedContent = useDocumentContentStore((state) =>
    documentUuid ? state.savedDocumentContent[documentUuid]?.[role] : undefined,
  );

  return useMemo(() => savedContent ?? [], [savedContent]);
};
